import { Vehicle } from '../model/Vehicle';

export type GtagFormTrackingInput = {
  orderPrice: number;
  vehicles: Array<Partial<Vehicle>>;
  quoteId?: string;
  paymentIntentId?: string;
  transactionId?: string;
};

export type GtagPageViewTrackingInput = {
  pagePath: string;
  screenName: string;
  pageTitle?: string;
};

export type GtagSessionTrackingInput = {
  eventName: string;
};

export type GtagConversionTrackingInput = {
  // No staging environment is available from the Google Analytics team.
  // Events will only be fired if the conversion ID is provided.
  conversionId?: string;

  // optional - fields are mainly used for purchase conversion event
  orderPrice?: number;
};

export type GtagFormEventPayload = {
  currency: string;
  value: number;
  items: Array<Partial<Vehicle>>;
  quote_id?: string;
  payment_intent_id?: string;
  transaction_id?: string;
};

export type GtagConversionEventPayload = {
  send_to: string;

  // optional - fields are mainly used for purchase conversion event
  value?: number;
  currency?: 'USD';
};

export type GtagPageViewEventPayload = {
  page_path: string;
  screen_name: string;
  page_title?: string;
};

export type GtagSessionEventPayload = {
  event_name: string;
};

type GtagEventMap = {
  add_vin_info: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  add_pickup_info: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  add_delivery_info: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  add_shipping_info: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  add_payment_info: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  begin_checkout: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  remove_from_cart: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  view_item: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  add_to_cart: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  view_cart: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  purchase: {
    input: GtagFormTrackingInput;
    payload: GtagFormEventPayload;
  };
  conversion: {
    input: GtagConversionTrackingInput;
    payload: GtagConversionEventPayload;
  };
  page_view: {
    input: GtagPageViewTrackingInput;
    payload: GtagPageViewEventPayload;
  };
  session: {
    input: GtagSessionTrackingInput;
    payload: GtagSessionEventPayload;
  };
};

export type GtagEventNames = keyof GtagEventMap;

type GtagEventsInput = {
  [K in keyof GtagEventMap]: {
    eventName: K;
    input: GtagEventMap[K]['input'];
  };
}[keyof GtagEventMap];

type GtagEventsPayload = {
  [K in keyof GtagEventMap]: {
    eventName: K;
    payload: GtagEventMap[K]['payload'];
  };
}[keyof GtagEventMap];

function buildGtagFormEventPayload(
  input: GtagFormTrackingInput
): GtagFormEventPayload {
  return {
    currency: 'USD',
    value: input.orderPrice,
    items: input.vehicles.map((vehicle) => ({
      item_id: vehicle.item_id,
      price: vehicle.price,
      item_name: vehicle.item_name,
      item_brand: vehicle.make,
      item_category: vehicle.model,
      item_category1: vehicle.year,
      vin: vehicle.vin,
      operational: vehicle.operational || false
    })),
    quote_id: input.quoteId,
    payment_intent_id: input.paymentIntentId,
    transaction_id: input.transactionId
  };
}

const sendGtagEvent = (event: GtagEventsPayload) => {
  if (typeof gtag === 'undefined') {
    console.error(
      'Configure gtag in index.html - see https://developers.google.com/tag-platform/gtagjs'
    );
    return;
  }

  gtag('event', event.eventName, event.payload);
  console.info('==== gtag ====');
  console.info(
    JSON.stringify(
      { eventName: event.eventName, payload: event.payload },
      null,
      2
    )
  );
};

const dispatch = (event: GtagEventsInput) => {
  switch (event.eventName) {
    case 'add_vin_info':
    case 'add_pickup_info':
    case 'add_delivery_info':
    case 'add_shipping_info':
    case 'add_payment_info':
    case 'begin_checkout':
    case 'remove_from_cart':
    case 'view_item':
    case 'add_to_cart':
    case 'view_cart':
    case 'purchase': {
      const payload = buildGtagFormEventPayload(event.input);
      sendGtagEvent({ eventName: event.eventName, payload });
      break;
    }

    case 'page_view': {
      const payload: GtagPageViewEventPayload = {
        page_path: event.input.pagePath,
        screen_name: event.input.screenName,
        page_title: event.input.pageTitle
      };

      sendGtagEvent({ eventName: event.eventName, payload });
      break;
    }

    case 'session': {
      const payload: GtagSessionEventPayload = {
        event_name: event.input.eventName
      };

      sendGtagEvent({ eventName: event.eventName, payload });
      break;
    }

    case 'conversion': {
      // Events will only be fired if the conversion ID is provided.
      // No staging environment is available from the Google Analytics team.
      if (!event.input.conversionId) {
        console.info('Conversion event skipped: no conversion ID provided');
        console.info('[SKIPPED] ==== gtag ====');
        console.info(
          JSON.stringify(
            { eventName: event.eventName, input: event.input },
            null,
            2
          )
        );
        return;
      }

      const payload: GtagConversionEventPayload = {
        send_to: event.input.conversionId
      };

      // checking for undefined since 0 is a valid value
      if (event.input.orderPrice !== undefined) {
        payload.value = event.input.orderPrice;
        payload.currency = 'USD';
      }

      sendGtagEvent({ eventName: event.eventName, payload });
      break;
    }

    default:
      console.warn('Unknown event name:', event);
  }
};

export const gtagService = {
  dispatch
};
